import { Box, Button, Card, Grid, ListSubheader, Typography } from "@material-ui/core";
import React, { useContext, useEffect, useState } from "react";
import { Contract, ContractEmployeesRequest, ContractStatus } from "../model/Contract";

import { AppContext } from "../context/AppContext";
import translate from "../i18n/Translator";

import { updateEmployees } from "../api/TenantContractAPI";
import { formatDate, stringToDatePlain } from "../components/DateFormat";
import { Employee } from "../model/Employee";
import { SEEmployeeRequest, SpecializedServicesEmployee } from "../model/CfdiSe";
import PrepareEmployees from "../se_commons/PrepareEmployees";
import { infoItem } from "./ContractDetails";
import { importGroupEmployee as importGroupEmployeeProvider } from "../api/ProviderEmployeeGroupAPI";
import { isRoleOrParent } from "../model/Role";
import { useEmployeeSnackbar } from "../se_commons/EmployeesSnackbar";

export interface ContractEmployeesParams {
    contract: Contract;
    employees: Employee[];
    queryStr: string;
    referer?: string;
    seEmployees?: SEEmployeeRequest[];
    onSubmit(contract: Contract): any;
    setSEEmployees(seEmployees: SEEmployeeRequest[]): any;
}

export default function ContractEmployees(props: ContractEmployeesParams) {
    const context = useContext(AppContext);
    const { showMessage } = useEmployeeSnackbar();
    const tenantId = context.session?.tenant?.id;
    const [submitting, setSubmitting] = useState(false);
    const [canEdit, setCanEdit] = useState<boolean>(true);
    const [seEmployeesBackup, setSEEmployeesBackup] = useState<SEEmployeeRequest[]>();
    const isContractProvider =
        isRoleOrParent(context.session!.role, "provider") && context.isGranted("ProviderContractsUpdate");

    const getMaxDate = () => {
        if(!props.contract.specialized_services?.date_undetermined && props.contract.specialized_services!.to){
            return new Date(props.contract.specialized_services!.to);
        }
        return undefined;
    }
    
    const [minDate] = useState<Date>(new Date(props.contract.specialized_services!.from));
    const [maxDate] = useState<Date | undefined>(getMaxDate());

    useEffect(() => {
        setCanEdit(ContractStatus.CLOSED !== props.contract.status);
        if (!tenantId) return;
        let i = 0;
        setSEEmployeesBackup(
            props.contract.specialized_services?.employees
                ?.filter((seEmployee) => Boolean(seEmployee.employee))
                .map((seEmployee) => {
                    return { ...convertSeEmployee(seEmployee, i++), deletable: !isContractProvider };
                })
        );
    }, [isContractProvider, props, tenantId]);

    const setEmployees = (seEmployees: SEEmployeeRequest[]) => {
        props.setSEEmployees(seEmployees);
    }

    const restartValues = () => {
        if(!seEmployeesBackup) return;
        props.setSEEmployees([...seEmployeesBackup]);
    }

    const invalidDatesEmployees = (seEmployees: SEEmployeeRequest[]) => {
        let overlaps = false;
        let outOfRange = false;
        let newEmployees = seEmployees
            .map(first => {
                if(first.to && first.from){
                    let firstFrom = stringToDatePlain(first.from!);
                    let firstTo = stringToDatePlain(first.to!);
                    if(invalidDateRanges(firstFrom, firstTo)){
                        first.inError = true;
                        outOfRange = true;
                    } else {
                        seEmployees
                        .forEach(second => {
                            if(first.employee_id === second.employee_id && first.external_id !== second.external_id){
                                let secondFrom = stringToDatePlain(second.from!);
                                let secondTo = stringToDatePlain(second.to!);
                                let fromOverlaps = !(firstTo < secondFrom);
                                let toOverlaps = !(firstFrom > secondTo);
                                if(toOverlaps && fromOverlaps){
                                    first.inError = true;
                                    overlaps = true;
                                }
                            }
                        });
                    }
                }
                return first;
            });
        if(outOfRange){
            showMessage(translate("se_contracts.view.warning_dates") as string, "warning");
            setEmployees(newEmployees);
        } else if(overlaps){
            showMessage(translate("se_contracts.view.warning_dates_overlaps") as string, "warning");
            setEmployees(newEmployees);
        }
        return overlaps || outOfRange;
    };

    const invalidDateRanges = (from: Date, to: Date) => {
        let beforeFrom = to < from;
        let isFromOutsidePeriod = outsidePeriod(from);
        let isToOutsidePeriod = outsidePeriod(to);
        return beforeFrom || isFromOutsidePeriod || isToOutsidePeriod;
    }

    const outsidePeriod = (date: Date) => {
        return date < props.contract.specialized_services!.from || 
            (!props.contract.specialized_services!.date_undetermined && 
                    date > props.contract.specialized_services!.to);
    }

    const onSubmit = () => {
        if(!props.seEmployees) return;
        if(invalidDatesEmployees(props.seEmployees)){
            return;
        }
        setSubmitting(true);
        updateEmployees(props.contract.tenant_id, props.contract.id, {
                employees: props.seEmployees
            } as ContractEmployeesRequest)
            .then((contract) => {
                showMessage(translate("se_contracts.view.success_update_employees") as string, "success");
                props.onSubmit(contract);
            })
            .catch(error => {
                showMessage(error.message, "error");
            })
            .finally(() => setSubmitting(false));
        ;
    }

    const promiseImportGroupEmployee = (file: File) => {
            return importGroupEmployeeProvider(context.session!.tenant!.id, props.contract.provider_id, file);
    };

    return (
        <Box py={2} px={2}>
            <Grid container justifyContent="space-between">
                <Grid item xs={12} >
                    <Card variant="outlined">
                        <Grid container>
                            <Grid item xs={12}>
                                <ListSubheader style={{ textTransform: "uppercase" }}>
                                    {translate(`se_contracts.view.contract_overdue`)}
                                </ListSubheader>
                            </Grid>
                            {infoItem({
                                value: formatDate(props.contract.specialized_services?.from, "DD/MM/YYYY"),
                                label: translate("contracts.from"),
                            })}
                            {infoItem({
                                value: props.contract.specialized_services?.date_undetermined
                                    ? translate("se_contracts.view.undefined")
                                    : formatDate(props.contract.specialized_services?.to, "DD/MM/YYYY"),
                                label: translate("contracts.to"),
                            })}
                        </Grid>
                    </Card>
                    <br/>
                    <PrepareEmployees 
                        minDate={minDate}
                        maxDate={maxDate}
                        submitting={submitting}
                        employees={props.employees}
                        grantedToUpdate={true}
                        seEmployees={props.seEmployees || []}
                        from={formatDate(props.contract.specialized_services!.from, "DD/MM/YYYY")}
                        to={formatDate(props.contract.specialized_services!.to, "DD/MM/YYYY")}
                        setEmployees={setEmployees}
                        repeatEmployees={true}
                        setSubmitting={setSubmitting}
                        promiseImportGroupEmployee={promiseImportGroupEmployee}
                        validateEmployees={invalidDatesEmployees}
                        canEdit={canEdit}
                    />
                    <Grid item xs={12}>
                    <Box pt={2}>
                        <Grid container justify="flex-end" spacing={2}>
                            <Grid item xs="auto">
                                <Button variant="outlined" color="secondary" size="medium" onClick={restartValues} disabled={submitting || !canEdit}>
                                    {translate("buttons.cancel")}
                                </Button>
                            </Grid>
                            <Grid item xs="auto">
                                <Button variant="outlined" color="primary" size="medium" onClick={onSubmit} disabled={submitting || !props.seEmployees || props.seEmployees!.length === 0 || !canEdit }>
                                    {translate("buttons.update")}
                                </Button>
                            </Grid>
                        </Grid>
                    </Box>
                </Grid>
                </Grid>
            </Grid>
        </Box>
    );
}

export const convertSeEmployee = (employeeResponse: SpecializedServicesEmployee, i: number) => {
    return {
        external_id: employeeResponse.employee.id+i,
        employee_id: employeeResponse.employee.id,
        from: formatDate(employeeResponse.from, "DD/MM/YYYY"),
        name: employeeResponse.employee.name,
        rfc: employeeResponse.employee.rfc,
        to: formatDate(employeeResponse.to, "DD/MM/YYYY"),
    } as SEEmployeeRequest;
}

